import type { VideoCallTeacherLinkStatus } from "~/types/videoCalls";

const GUEST_SESSION_KEY = "teachplus.video-call.guest-session";
const HOST_INVITATION_KEY = "teachplus.video-call.host-invitation";
export const VIDEO_CALL_TOKEN_CAPTURED_EVENT =
  "teachplus:video-call-token-captured";

declare global {
  interface Window {
    __teachplusVideoCallInviteInvalid?: boolean;
    __teachplusVideoCallInviteToken?: string;
  }
}

export interface CapturedVideoCallInvitation {
  token: string;
  invalid: boolean;
}

let capturedVideoCallInvitation: CapturedVideoCallInvitation | null = null;

export interface StoredVideoCallGuestSession {
  joinRequestId: string;
  guestSecret: string;
  callId: string;
  name: string;
  backgroundNavigationEnabled?: boolean;
  individualAccountSignupAvailable?: boolean;
  teacherLinkStatus?: VideoCallTeacherLinkStatus;
}

export interface StoredVideoCallHostInvitation {
  callId: string;
  invitationId?: string;
  token: string;
  expiresAt: string;
  shareUrl?: string;
}

const getSessionStorage = (): Storage | null => {
  if (!import.meta.client) {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const readJson = <T>(key: string): T | null => {
  const storage = getSessionStorage();
  if (!storage) {
    return null;
  }

  try {
    const value = storage.getItem(key);
    if (!value) {
      return null;
    }
    return JSON.parse(value) as T;
  } catch {
    try {
      storage.removeItem(key);
    } catch {
      // Storage can be blocked by browser privacy settings.
    }
    return null;
  }
};

export const getStoredVideoCallGuestSession =
  (): StoredVideoCallGuestSession | null =>
    readJson<StoredVideoCallGuestSession>(GUEST_SESSION_KEY);

export const hasStoredVideoCallGuestBackgroundSession = (): boolean => {
  const stored = getStoredVideoCallGuestSession();
  return (
    stored?.backgroundNavigationEnabled === true &&
    Boolean(stored.joinRequestId && stored.guestSecret && stored.callId)
  );
};

export const setStoredVideoCallGuestSession = (
  value: StoredVideoCallGuestSession,
) => {
  try {
    getSessionStorage()?.setItem(GUEST_SESSION_KEY, JSON.stringify(value));
  } catch {
    // The active call still works without reload restoration.
  }
};

export const clearStoredVideoCallGuestSession = () => {
  try {
    getSessionStorage()?.removeItem(GUEST_SESSION_KEY);
  } catch {
    // Teardown must continue even when storage is blocked.
  }
};

export const getStoredVideoCallHostInvitation =
  (): StoredVideoCallHostInvitation | null =>
    readJson<StoredVideoCallHostInvitation>(HOST_INVITATION_KEY);

export const setStoredVideoCallHostInvitation = (
  value: StoredVideoCallHostInvitation,
) => {
  try {
    getSessionStorage()?.setItem(HOST_INVITATION_KEY, JSON.stringify(value));
  } catch {
    // The link remains available for the current in-memory session.
  }
};

export const clearStoredVideoCallHostInvitation = () => {
  try {
    getSessionStorage()?.removeItem(HOST_INVITATION_KEY);
  } catch {
    // Teardown must continue even when storage is blocked.
  }
};

export const captureVideoCallToken = (
  token: string,
  options: { invalid?: boolean } = {},
) => {
  capturedVideoCallInvitation = {
    token: options.invalid ? "" : token,
    invalid: options.invalid === true,
  };
};

export const readAndClearVideoCallInvitation =
  (): CapturedVideoCallInvitation => {
    if (!import.meta.client) {
      return { token: "", invalid: false };
    }

    const capturedInvitation = capturedVideoCallInvitation;
    capturedVideoCallInvitation = null;
    if (capturedInvitation) {
      return capturedInvitation;
    }

    const preflightToken = window.__teachplusVideoCallInviteToken ?? "";
    const preflightInvalid = window.__teachplusVideoCallInviteInvalid === true;
    delete window.__teachplusVideoCallInviteToken;
    delete window.__teachplusVideoCallInviteInvalid;

    if (preflightToken || preflightInvalid) {
      return {
        token: preflightInvalid ? "" : preflightToken,
        invalid: preflightInvalid,
      };
    }

    if (!window.location.hash) {
      return { token: "", invalid: false };
    }

    const hash = window.location.hash.slice(1);
    const params = new URLSearchParams(hash);
    const parameterToken = params.get("token");
    const token = parameterToken === null ? hash : parameterToken;
    window.history.replaceState(
      window.history.state,
      document.title,
      `${window.location.pathname}${window.location.search}`,
    );
    return { token, invalid: false };
  };

export const readAndClearVideoCallTokenFromHash = (): string =>
  readAndClearVideoCallInvitation().token;
